import React, { useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useRecoilValue } from 'recoil'

import tooltip from 'react-tooltip'

import {
  Badge,
  BADGE_TYPE_DEFAULT,
  BADGE_TYPE_INFO,
  BADGE_TYPE_WARNING,
  BADGE_TYPE_ERROR,
  BADGE_KIND_PRIMARY,
  BADGE_KIND_SECONDARY,
  Panel,
  HelpIcon,
  Divider,
  Flex,
  Link,
  LINK_VARIANT_EXTRALIGHT,
  LINK_VARIANT_HIGHLIGHT,
  IconFACheck,
  Title,
  TITLE_VARIANT_S
} from 'mailup-web-components'

import { checkupOKState, messageState } from '../recoil/state/message'

import styled from '@emotion/styled'

const CheckUpBoxItem = ({
  label,
  labelAttributeTest,
  count,
  countAttributeTest,
  issueType,
  onClick
}) => {
  return (
    <>
      <Flex justifyContent='space-between' alignItems='center'>
        <Link
          variant={LINK_VARIANT_HIGHLIGHT}
          disabled={!(count > 0)}
          onClick={onClick}
          data-mu-test={labelAttributeTest}
        >
          {label}
        </Link>
        <Badge
          type={count > 0 ? issueType : BADGE_TYPE_DEFAULT}
          kind={count > 0 ? BADGE_KIND_PRIMARY : BADGE_KIND_SECONDARY}
          text={count}
          onClick={count > 0 ? onClick : undefined}
          data-mu-test={countAttributeTest}
        />
      </Flex>
    </>
  )
}

CheckUpBoxItem.propTypes = {
  label: PropTypes.string,
  count: PropTypes.number,
  issueType: PropTypes.string,
  onClick: PropTypes.func,
  labelAttributeTest: PropTypes.string,
  countAttributeTest: PropTypes.string
}

const CheckUpBox = () => {
  const intl = useIntl()
  const history = useHistory()
  const { id, checkup } = useRecoilValue(messageState)
  const checkupOK = useRecoilValue(checkupOKState)

  useEffect(() => {
    tooltip.rebuild()
  }, [checkup])

  const goToCheckup = useCallback(() => {
    history.push(`/messages/email/${id}/checkup/summary`)
  }, [id])

  const goToLinkCheckup = useCallback(() => {
    const route = `/messages/email/${id}/checkup/linkcheck`
    history.push(route)
  }, [id])

  return checkup ? (
    <Panel data-mu-test='overview-message-box-checkup'>
      <Flex
        justifyContent='space-between'
        alignItems='center'
        margin='0 0 24px 0'
      >
        <Title variant={TITLE_VARIANT_S}>
          {intl.formatMessage({ id: 'Load.EmailCheckUp.MessageCheckup' })}
        </Title>
        {checkupOK && <CheckupOkIcon />}
      </Flex>
      <Flex flexDirection='column' rowGap='8px' margin='0 0 8px 0'>
        <CheckUpBoxItem
          label={intl.formatMessage({ id: 'Load.EmailCheckUp.Problem' })}
          count={checkup.blocking}
          onClick={goToCheckup}
          issueType={BADGE_TYPE_ERROR}
          labelAttributeTest='go-to-checkup-problem'
          countAttributeTest='go-to-checkup-problem-count'
        />
        <CheckUpBoxItem
          label={intl.formatMessage({ id: 'Load.EmailCheckUp.Critique' })}
          count={checkup.potential}
          onClick={goToCheckup}
          issueType={BADGE_TYPE_WARNING}
          labelAttributeTest='go-to-checkup-critique'
          countAttributeTest='go-to-checkup-critique-count'
        />
        <CheckUpBoxItem
          label={intl.formatMessage({ id: 'Load.EmailCheckUp.BestPractice' })}
          count={checkup.improvements}
          onClick={goToCheckup}
          issueType={BADGE_TYPE_INFO}
          labelAttributeTest='go-to-checkup-best-practice'
          countAttributeTest='go-to-checkup-best-practice-count'
        />
      </Flex>

      <Divider />

      <Flex margin='8px 0' columnGap='4px' height='24px' alignItems='flex-end'>
        <Link
          variant={LINK_VARIANT_EXTRALIGHT}
          onClick={goToLinkCheckup}
          data-mu-test='go-to-link-check'
        >
          {intl.formatMessage({ id: 'Load.EmailCheckUp.ChechYourLink' })}
        </Link>
        <HelpIcon
          text={intl.formatMessage({
            id: 'Load.EmailCheckUp.ChechYourLinkTooltip'
          })}
        />
      </Flex>
    </Panel>
  ) : null
}

export default CheckUpBox

const CheckupOkIcon = styled(IconFACheck)`
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: inline-grid;
  place-content: center;
  background-color: var(--tea-green);
  color: var(--sushi);

  svg {
    top: 0 !important;
    width: 12px;
    height: 12px;
  }
`
